import { Link } from "react-router-dom";
import { Building2, CheckCircle2, PauseCircle, Hourglass, CalendarX2, Users, Globe, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Kpi, PageHeader, Panel, RowsSkeleton, StatusBadge, EmptyState } from "@/components/app/kit";
import { useFetch } from "@/hooks/useServerList";
import { fmtDate } from "@/lib/i18n";

const PlanBars = ({ data, total }) =>
  data.length === 0 ? <EmptyState title="Aucune formule attribuée" text="Les répartitions apparaîtront après la création des cabinets." /> : (
    <div className="space-y-4">
      {data.map((p) => (
        <div key={p.plan_code} data-testid={`plan-distribution-${p.plan_code}`}>
          <div className="flex justify-between text-sm mb-1.5"><span className="font-medium text-slate-700">{p.plan_name}</span><span className="font-mono text-slate-500">{p.count}</span></div>
          <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-sky-600 rounded-full transition-[width] duration-700" style={{ width: `${(p.count / Math.max(total, 1)) * 100}%` }} /></div>
        </div>
      ))}
    </div>
  );

export default function PlatformDashboard() {
  const { data, loading } = useFetch("/platform/dashboard");
  return (
    <div>
      <PageHeader eyebrow="Vue d'ensemble" title="Tableau de bord global" subtitle="Indicateurs calculés en temps réel à partir de la base de données."
        actions={<Button asChild data-testid="dashboard-create-tenant"><Link to="/admin/cabinets/nouveau"><Plus className="h-4 w-4 mr-2" />Créer un cabinet</Link></Button>} />
      {loading || !data ? <RowsSkeleton rows={6} /> : (
        <div className="space-y-6 animate-fade-up">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <Kpi label="Cabinets" value={data.tenants_total} icon={Building2} testid="kpi-tenants-total" />
            <Kpi label="Actifs" value={data.tenants_active} icon={CheckCircle2} tone="green" testid="kpi-tenants-active" />
            <Kpi label="Essais en cours" value={data.tenants_trial} icon={Hourglass} tone="amber" testid="kpi-tenants-trial" />
            <Kpi label="Suspendus" value={data.tenants_suspended} icon={PauseCircle} tone="red" testid="kpi-tenants-suspended" />
            <Kpi label="Abonnements expirés" value={data.tenants_expired} icon={CalendarX2} tone="gray" testid="kpi-tenants-expired" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <Panel title="Répartition des formules" className="lg:col-span-5" testid="plan-distribution">
              <PlanBars data={data.by_plan} total={data.tenants_total} />
            </Panel>
            <Panel title="Derniers cabinets créés" className="lg:col-span-7">
              {data.recent_tenants.length === 0 ? <EmptyState icon={Building2} title="Aucun cabinet" text="Créez le premier cabinet de la plateforme." /> : (
                <ul className="divide-y divide-slate-100 -my-2">
                  {data.recent_tenants.map((t) => (
                    <li key={t.id} className="py-3 flex items-center justify-between gap-3">
                      <Link to={`/admin/cabinets/${t.id}`} className="min-w-0 hover:text-sky-700" data-testid={`recent-tenant-${t.id}`}>
                        <p className="font-semibold text-sm truncate">{t.name}</p>
                        <p className="text-xs text-slate-500">{t.city} · {fmtDate(t.created_at)}</p>
                      </Link>
                      <StatusBadge status={t.status} />
                    </li>
                  ))}
                </ul>
              )}
            </Panel>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <Panel title="Échéances dans les 30 jours" className="lg:col-span-7">
              {data.expiring_soon.length === 0 ? <p className="text-sm text-slate-500">Aucune échéance proche.</p> : (
                <ul className="divide-y divide-slate-100 -my-2">
                  {data.expiring_soon.map((t) => (
                    <li key={t.id} className="py-3 flex justify-between text-sm"><Link to={`/admin/cabinets/${t.id}`} className="font-medium hover:text-sky-700">{t.name}</Link><span className="text-slate-500">{fmtDate(t.end_date)}</span></li>
                  ))}
                </ul>
              )}
            </Panel>
            <div className="lg:col-span-5 grid grid-cols-2 gap-4">
              <Kpi label="Utilisateurs des cabinets" value={data.users_total} icon={Users} tone="indigo" testid="kpi-users-total" />
              <Kpi label="Domaines à vérifier" value={data.domains_pending} icon={Globe} tone="amber" testid="kpi-domains-pending" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
